import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Hero from './Hero';
import ContentBlock from './ContentBlock';
import InstagramFeed from './InstagramFeed';
import ProductCard from './ProductCard';
import PromotionalBanner from './PromotionalBanner';
import HomeWindowStrip from './HomeWindowStrip';
import { Product, HeroSlide, PromotionalBanner as PromotionalBannerType, SeasonalBanner } from '../types';
import { ARTICLES, HERO_SLIDES } from '../constants';
import { getWindowState } from '../utils/productWindows';

interface HomeProps {
  onProductClick?: (product: Product) => void;
}

interface BrandSectionSettings {
  backgroundImage: string;
  overlayOpacity: number;
  headline: string;
  description: string;
  ctaText: string;
  ctaLink: string;
}

const Home: React.FC<HomeProps> = ({ onProductClick }) => {
  const [heroSlides, setHeroSlides] = useState<HeroSlide[]>(HERO_SLIDES);
  const [articles, setArticles] = useState(ARTICLES);
  const [latestProducts, setLatestProducts] = useState<Product[]>([]);
  const [promotionalBanner, setPromotionalBanner] = useState<PromotionalBannerType | null>(null);
  // Seasonal banner has no defaults — it renders only if the CMS enables it
  const [seasonalBanner, setSeasonalBanner] = useState<SeasonalBanner>({
    visible: false,
    backgroundImage: '',
    overlayOpacity: 40,
    eyebrow: '',
    title: '',
    subtitle: '',
    ctaText: '',
    ctaLink: '',
  });
  const [brandSection, setBrandSection] = useState<BrandSectionSettings>({
    backgroundImage: '',
    overlayOpacity: 90,
    headline: 'THE ARCHIVE',
    description: 'Recovered graphics, blank garments, single decks. Everything is made when claimed.',
    ctaText: 'ENTER THE ARCHIVE',
    ctaLink: '/shop',
  });

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const { collection, getDocs, query, orderBy, limit, where } = await import('firebase/firestore');
        const { doc, getDoc } = await import('firebase/firestore');
        const { db } = await import('../utils/firebase');

        // 1. Fetch Hero Slides and Brand Sections
        const docRef = doc(db, 'content', 'home');
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const data = docSnap.data();
          if (data.heroSlides) {
            setHeroSlides(data.heroSlides);
          }
          if (data.promotionalBanner) {
            setPromotionalBanner(data.promotionalBanner);
          }
          if (data.seasonalBanner) {
            setSeasonalBanner((prev) => ({ ...prev, ...data.seasonalBanner }));
          }
          if (data.brandSections && data.brandSections['4HVNSK8']) {
            setBrandSection((prev) => ({ ...prev, ...data.brandSections['4HVNSK8'] }));
          }
        }

        // 2. Fetch Latest Articles (Limit 3)
        const articlesQ = query(collection(db, 'articles'), orderBy('publishDate', 'desc'), limit(3));
        const articlesSnap = await getDocs(articlesQ);
        if (!articlesSnap.empty) {
          const fetchedArticles: any[] = [];
          articlesSnap.forEach(doc => {
            const data = doc.data();
            if (data.visible !== false) {
              fetchedArticles.push({ id: doc.id, ...data });
            }
          });
          setArticles(fetchedArticles);
        }

        // 3. Recent recoveries — only the tiers that surface (fragments +
        // artifacts), unclosed windows only, newest first. The Uniform is
        // the constant; its home on this page is the feature band.
        const productsQ = query(
          collection(db, 'products'),
          where('tier', 'in', ['fragment', 'artifact'])
        );
        const productsSnap = await getDocs(productsQ);

        const nowDate = new Date();
        const fetched: Product[] = [];
        productsSnap.forEach(doc => {
          const product = { id: doc.id, ...(doc.data() as Product) };
          if (product.status === 'sold_out') return; // claimed artifacts / legacy
          if (getWindowState(product, nowDate) === 'closed') return;
          fetched.push(product);
        });

        const millis = (t: any) => (t?.toMillis ? t.toMillis() : t?.seconds ? t.seconds * 1000 : 0);
        fetched.sort((a: any, b: any) => millis(b.createdAt) - millis(a.createdAt));
        setLatestProducts(fetched.slice(0, 4));

      } catch (error) {
        console.error("Error fetching home content:", error);
      }
    };
    fetchContent();
  }, []);

  return (
    <>
      <Hero slides={heroSlides} />

      {/* PROMOTIONAL CONTENT BANNER */}
      {promotionalBanner && <PromotionalBanner banner={promotionalBanner} />}

      {/* ORIENTATION — what this place is */}
      <section className="py-16 px-4 md:px-8 max-w-[1400px] mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-8">
          <div>
            <h3 className="font-heading text-sm font-bold tracking-[0.2em] uppercase mb-3">Fragments</h3>
            <p className="font-mono text-xs text-gray-600 leading-relaxed">
              Fragments are recovered graphics, reissued once. Open for seven days. Then closed, permanently.
            </p>
          </div>
          <div>
            <h3 className="font-heading text-sm font-bold tracking-[0.2em] uppercase mb-3">Standard Issue</h3>
            <p className="font-mono text-xs text-gray-600 leading-relaxed">
              Standard Issue is what the crew wore: nothing printed. Always available. Finished by hand.
            </p>
          </div>
          <div>
            <h3 className="font-heading text-sm font-bold tracking-[0.2em] uppercase mb-3">Artifacts</h3>
            <p className="font-mono text-xs text-gray-600 leading-relaxed">
              Artifacts surface rarely. One of each exists.
            </p>
          </div>
        </div>
      </section>

      {/* THE CLOCK — the next window, stated plainly */}
      <HomeWindowStrip />

      {/* RECENT RECOVERIES */}
      {latestProducts.length > 0 && (
        <section className="py-20 px-4 md:px-8 max-w-[1400px] mx-auto">
          <div className="mb-8">
            <h3 className="font-display text-2xl font-normal tracking-widest">Recent recoveries</h3>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {latestProducts.map(product => (
              <ProductCard
                key={product.id}
                product={product}
                onClick={onProductClick}
              />
            ))}
          </div>
          <Link
            to="/shop"
            className="inline-block mt-6 font-heading text-sm tracking-widest underline hover:no-underline"
          >
            VIEW ALL
          </Link>
        </section>
      )}

      {/* FEATURE SECTION */}
      <section className="p-12 lg:p-20 min-h-[500px] lg:min-h-[70vh] flex flex-col justify-center relative overflow-hidden bg-brand-ink text-white">
        {/* Background Image */}
        {brandSection.backgroundImage && (
          <img
            src={brandSection.backgroundImage}
            alt=""
            onError={(e) => {
              e.currentTarget.style.display = 'none';
            }}
            className="absolute inset-0 w-full h-full object-cover"
          />
        )}
        {/* Overlay */}
        <div
          className="absolute inset-0 bg-gradient-to-br from-brand-ink to-brand-charcoal"
          style={{ opacity: brandSection.overlayOpacity / 100 }}
        />
        <div className="relative z-10 max-w-[1400px] mx-auto w-full">
          <h2 className="font-display text-3xl lg:text-5xl font-normal tracking-widest uppercase mb-6 text-white">
            {brandSection.headline.split('\n').map((line, i) => (
              <React.Fragment key={i}>
                {line}
                {i < brandSection.headline.split('\n').length - 1 && <br />}
              </React.Fragment>
            ))}
          </h2>
          <p className="font-body text-white/70 mb-8 max-w-md text-sm lg:text-base">
            {brandSection.description}
          </p>
          <Link
            to={brandSection.ctaLink}
            className="inline-block border border-white px-8 py-3 font-heading tracking-widest text-sm text-white hover:bg-white hover:text-black transition-all duration-300"
          >
            {brandSection.ctaText}
          </Link>
        </div>
      </section>

      {/* SEASONAL CAMPAIGN BANNER */}
      {seasonalBanner.visible && (
        <section className="relative h-[400px] md:h-[500px] overflow-hidden">
          <img
            src={seasonalBanner.backgroundImage || '/assets/placeholder.svg'}
            alt={seasonalBanner.title || 'Seasonal Campaign'}
            onError={(e) => {
              const t = e.currentTarget;
              if (!t.dataset.fallback) { t.dataset.fallback = '1'; t.src = '/assets/placeholder.svg'; }
            }}
            className="w-full h-full object-cover"
          />
          <div
            className="absolute inset-0 bg-black"
            style={{ opacity: seasonalBanner.overlayOpacity / 100 }}
          />
          <div className="absolute inset-0 flex items-center justify-center text-center text-white px-4">
            <div className="space-y-4">
              {seasonalBanner.eyebrow && (
                <span className="font-heading text-[10px] tracking-[0.3em] uppercase text-white/70">
                  {seasonalBanner.eyebrow}
                </span>
              )}
              <h2 className="font-heading text-4xl md:text-6xl font-bold tracking-widest">
                {seasonalBanner.title}
              </h2>
              {seasonalBanner.subtitle && (
                <p className="font-heading text-sm tracking-[0.3em]">{seasonalBanner.subtitle}</p>
              )}
              {seasonalBanner.ctaText && (
                <Link
                  to={seasonalBanner.ctaLink || '/shop/soon'}
                  className="inline-block border border-white px-10 py-3 mt-4 font-heading tracking-widest text-sm hover:bg-white hover:text-black transition-all duration-300"
                >
                  {seasonalBanner.ctaText}
                </Link>
              )}
            </div>
          </div>
        </section>
      )}

      {/* ARCHIVE ENTRIES FEED */}
      {articles.length > 0 && (
        <section className="py-20 px-4 md:px-8 max-w-[1400px] mx-auto">
          <div className="flex justify-between items-end mb-12 border-b border-black pb-4">
            <h2 className="font-heading text-4xl md:text-6xl font-bold tracking-tighter">LATEST</h2>
            <span className="font-mono text-xs md:text-sm tracking-widest uppercase">From the archive</span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-x-4 gap-y-12">
            {articles.map((article, index) => (
              <div key={article.id} className="md:col-span-6 lg:col-span-4">
                <ContentBlock article={article} featured={index === 0} />
              </div>
            ))}
          </div>
        </section>
      )}

      <InstagramFeed />
    </>
  );
};

export default Home;
